import moment from 'moment/moment'
import { useState } from 'react'

import { EuiDatePicker, EuiFieldNumber, EuiFieldText, EuiFormRow, EuiRadioGroup, EuiTextArea } from '@elastic/eui'

import { SegmentsForm } from 'components/targeting/TargetingForm/SegmentsForm'
import { getCampaignTypeNameLong } from 'utils/CampaignType'

import { IntervalUnit, LocationPostalCode, PostalCodeTarget, SegmentTarget } from '../../../api/interfaces'
import { MasterCampaignPlanCampaignBuilderType } from '../../../api/interfaces/MasterCampaignStub'
import { AcTargeterSelection } from '../../../components/targeting/finders/AcTargeterSelection'
import { PostalCodeFinder } from '../../../components/targeting/finders/PostalCodeFinder'

export enum AddCampaignSectionStepOld {
    CampaignType = 'CampaignType',
    CampaignDetails = 'CampaignDetails'
}

export interface CampaignFormOldProps {
    campaignBuilder: CampaignFormFieldsOld
    setCampaignBuilder: (campaignBuilder: CampaignFormFieldsOld) => void
    step: AddCampaignSectionStepOld
}

export interface CampaignFormFieldsOld {
    name: string
    shortSummary: string
    longSummary: string
    audienceSummary: string
    geoSummary: string
    seasonalitySummary: string
    campaignType: MasterCampaignPlanCampaignBuilderType
    intervalBudget?: number
    retargetSite: boolean
    startDate: moment.Moment
    intervalUnit: IntervalUnit
    intervalFrequency: number
    intervalDuration: number
    segmentTargets: SegmentTarget[]
    zips: PostalCodeTarget[]
}

export const CampaignFormOld = ({ campaignBuilder, setCampaignBuilder, step }: CampaignFormOldProps) => {
    const [budgetTouched, setBudgetTouched] = useState(false)

    return (
        <>
            {step === AddCampaignSectionStepOld.CampaignType && (
                <EuiFormRow label='Select Campaign Type'>
                    <EuiRadioGroup
                        options={[
                            { id: MasterCampaignPlanCampaignBuilderType.TV, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.TV as any) },
                            { id: MasterCampaignPlanCampaignBuilderType.Billboard, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Billboard as any) },
                            { id: MasterCampaignPlanCampaignBuilderType.Internet, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Internet as any) }
                        ]}
                        idSelected={campaignBuilder.campaignType}
                        onChange={id => setCampaignBuilder({ ...campaignBuilder, campaignType: id as MasterCampaignPlanCampaignBuilderType })}
                        name='campaign type group'
                    />
                </EuiFormRow>
            )}
            {step === AddCampaignSectionStepOld.CampaignDetails && (
                <>
                    <EuiFormRow fullWidth label='Campaign Name *' isInvalid={campaignBuilder.name === ''} error={['Campaign name is required']}>
                        <EuiFieldText fullWidth value={campaignBuilder.name} onChange={e => setCampaignBuilder({ ...campaignBuilder, name: e.target.value })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Campaign Purpose (used in campaign preview)'>
                        <EuiTextArea fullWidth value={campaignBuilder.shortSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, shortSummary: e.target.value })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Extended Campaign Purpose (used in PDF documents)'>
                        <EuiTextArea fullWidth value={campaignBuilder.longSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, longSummary: e.target.value })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Audience Summary (used in PDF documents)'>
                        <EuiTextArea fullWidth value={campaignBuilder.audienceSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, audienceSummary: e.target.value })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Geo Summary (used in PDF documents)'>
                        <EuiTextArea fullWidth value={campaignBuilder.geoSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, geoSummary: e.target.value })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Seasonality Summary (used in PDF documents)'>
                        <EuiTextArea fullWidth value={campaignBuilder.seasonalitySummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, seasonalitySummary: e.target.value })} />
                    </EuiFormRow>

                    {(campaignBuilder.campaignType === MasterCampaignPlanCampaignBuilderType.Internet || campaignBuilder.campaignType === MasterCampaignPlanCampaignBuilderType.TV) && (
                        <EuiFormRow fullWidth label='Audience Segments'>
                            <SegmentsForm
                                campaignType={campaignBuilder.campaignType as any}
                                segments={campaignBuilder.segmentTargets}
                                onSegmentsChange={segmentTargets =>
                                    setCampaignBuilder({
                                        ...campaignBuilder,
                                        segmentTargets
                                    })
                                }
                            />
                        </EuiFormRow>
                    )}

                    <EuiFormRow fullWidth label='Zip Codes'>
                        <PostalCodeFinder
                            onOptionClick={(zip: LocationPostalCode) =>
                                setCampaignBuilder({
                                    ...campaignBuilder,
                                    zips: [...(campaignBuilder.zips ?? []), zip]
                                })
                            }
                        />
                    </EuiFormRow>

                    <EuiFormRow fullWidth>
                        <AcTargeterSelection
                            items={campaignBuilder.zips?.map(r => ({ key: r.id.toString(), label: r.name })) ?? []}
                            onItemRemoved={item => {
                                setCampaignBuilder({
                                    ...campaignBuilder,
                                    zips: campaignBuilder.zips?.filter(r => r.id.toString() !== item.key)
                                })
                            }}
                        />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Start Date' isInvalid={moment(campaignBuilder.startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3} error={['Start date must be at least 3 days from today']}>
                        <EuiDatePicker minDate={moment().add(3, 'days')} selected={moment(campaignBuilder.startDate)} onChange={date => setCampaignBuilder({ ...campaignBuilder, startDate: date! })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Duration' isInvalid={campaignBuilder.intervalDuration <= 0} error={['Duration must be greater than 0']}>
                        <EuiFieldNumber value={campaignBuilder.intervalDuration} append={`${campaignBuilder.intervalFrequency > 1 ? campaignBuilder.intervalFrequency : ''} ${campaignBuilder.intervalUnit}${campaignBuilder.intervalDuration > 1 ? 's' : ''}`} onChange={e => setCampaignBuilder({ ...campaignBuilder, intervalDuration: parseInt(e.target.value) })} />
                    </EuiFormRow>

                    <EuiFormRow fullWidth label='Budget *' isInvalid={budgetTouched && ((!!campaignBuilder.intervalBudget && campaignBuilder.intervalBudget < 100) || !campaignBuilder.intervalBudget)} error={['Budget must be at least $100']}>
                        <EuiFieldNumber
                            prepend={'$'}
                            value={campaignBuilder.intervalBudget}
                            min={100}
                            onClick={() => {
                                setBudgetTouched(true)
                            }}
                            onChange={e =>
                                setCampaignBuilder({
                                    ...campaignBuilder,
                                    intervalBudget: Number(e.target.value)
                                })
                            }
                            append={`per ${campaignBuilder.intervalUnit}${campaignBuilder.intervalFrequency > 1 ? 's' : ''}`}
                        />
                    </EuiFormRow>
                </>
            )}
        </>
    )
} 